// EU AI Act preliminary classification engine — v3 (i18n-aware).
//
// Same legal logic as v2 (Art. 5 prohibited screen, Art. 6(3) profiling
// override, four 6(3) carve-outs, phased timeline, GPAI provider path),
// now with English + German strings co-located so a single source-of-truth
// drives both renders. Lookup helper is pickLang() in ./i18n.js.
//
// German legal terminology follows the official EU translation:
//   provider          → "Anbieter"
//   deployer          → "Betreiber"
//   high-risk         → "Hochrisiko-KI-System"
//   prohibited        → "verbotene Praktiken"
//   AI literacy       → "KI-Kompetenz"
//   FRIA              → "Grundrechte-Folgenabschätzung"
//   conformity assessment → "Konformitätsbewertung"

import { pickLang } from "./i18n";

// ---------- Phased dates (Art. 113) ----------

export const DATE_ART5_PROHIBITIONS = new Date("2025-02-02T00:00:00Z");
export const DATE_GPAI_PROVIDER     = new Date("2025-08-02T00:00:00Z");
export const DATE_ANNEX_III         = new Date("2026-08-02T00:00:00Z");
export const DATE_ANNEX_I           = new Date("2027-08-02T00:00:00Z");

// ---------- Question definitions ----------

export const QUESTIONS = [
  {
    id: "country",
    type: "single",
    text: {
      en: "Where does your AI activity touch the EU?",
      de: "Wo berührt Ihre KI-Tätigkeit die EU?",
    },
    help: {
      en: "Article 2 makes the Act apply to anyone established in the EU, anyone outside the EU placing AI on the EU market, and any deployer or provider whose AI output is used in the EU. Even non-EU SaaS reaches the Act if the output reaches EU users.",
      de: "Artikel 2 erfasst alle in der EU niedergelassenen Akteure, alle außerhalb der EU, die KI auf dem EU-Markt anbieten, sowie Anbieter und Betreiber, deren KI-Ergebnisse in der EU verwendet werden. Auch SaaS außerhalb der EU fällt unter die Verordnung, wenn die Ausgabe EU-Nutzer erreicht.",
    },
    options: [
      { value: "at", label: { en: "We're established in Austria", de: "Wir sind in Österreich niedergelassen" } },
      { value: "de", label: { en: "We're established in Germany", de: "Wir sind in Deutschland niedergelassen" } },
      { value: "eu", label: { en: "We're established in another EU member state", de: "Wir sind in einem anderen EU-Mitgliedstaat niedergelassen" } },
      { value: "non_eu_serves_eu", label: { en: "We're outside the EU, but our AI's output reaches EU users or customers", de: "Wir sind außerhalb der EU, doch unsere KI-Ergebnisse erreichen EU-Nutzer oder -Kunden" } },
      { value: "non_eu_no_eu", label: { en: "We're outside the EU, no EU customers, no EU output", de: "Wir sind außerhalb der EU, ohne EU-Kunden und ohne EU-Ausgabe" } },
    ],
  },
  {
    id: "size",
    type: "single",
    text: { en: "How many people does your company employ?", de: "Wie viele Mitarbeitende hat Ihr Unternehmen?" },
    help: {
      en: "Doesn't change your classification — but Art. 99 caps SME fines at the lowest applicable percentage.",
      de: "Ändert die Einstufung nicht — Artikel 99 deckelt jedoch Bußgelder für KMU auf den jeweils niedrigeren Prozentsatz.",
    },
    options: [
      { value: "1_9", label: { en: "1–9 (micro)", de: "1–9 (Kleinst)" } },
      { value: "10_49", label: { en: "10–49 (small)", de: "10–49 (klein)" } },
      { value: "50_249", label: { en: "50–249 (medium)", de: "50–249 (mittel)" } },
      { value: "250_plus", label: { en: "250 or more", de: "250 oder mehr" } },
    ],
  },
  {
    id: "role",
    type: "single",
    text: { en: "What's your relationship to AI?", de: "In welcher Rolle haben Sie mit KI zu tun?" },
    help: {
      en: "Provider (Art. 3(3)) means you develop or commission an AI system and put it on the EU market under your own name. Deployer (Art. 3(4)) means you use AI in your business under your own authority — even if it's a vendor's tool.",
      de: "Anbieter (Art. 3(3)): Sie entwickeln oder lassen ein KI-System entwickeln und bringen es unter eigenem Namen auf den EU-Markt. Betreiber (Art. 3(4)): Sie nutzen KI in Ihrem Unternehmen unter eigener Verantwortung — auch wenn es das Tool eines Anbieters ist.",
    },
    options: [
      { value: "deployer", label: { en: "We use AI tools built by others (Copilot, ChatGPT, vendor SaaS, etc.)", de: "Wir nutzen KI-Tools anderer Anbieter (Copilot, ChatGPT, Vendor-SaaS usw.)" } },
      { value: "provider", label: { en: "We build or fine-tune AI systems and place them on the EU market", de: "Wir entwickeln oder feintunen KI-Systeme und bringen sie auf den EU-Markt" } },
      { value: "both", label: { en: "Both — we build AI and we use third-party AI", de: "Beides — wir entwickeln KI und nutzen Drittanbieter-KI" } },
      { value: "none", label: { en: "We don't use AI in any meaningful way", de: "Wir nutzen KI nicht in nennenswerter Weise" } },
    ],
  },
  {
    id: "prohibited",
    type: "multi",
    text: {
      en: "Does any of this match how your AI is used? Pick all that apply.",
      de: "Trifft eines davon auf Ihre KI-Nutzung zu? Mehrfachauswahl möglich.",
    },
    help: {
      en: "These uses are banned outright — you cannot place them on the EU market or operate them in the EU. The ban is in force since 2 February 2025 and the maximum fine is €35M or 7% of global turnover. Pick anything that even arguably matches.",
      de: "Diese Praktiken sind ausdrücklich verboten — sie dürfen weder auf dem EU-Markt angeboten noch in der EU betrieben werden. Das Verbot gilt seit 2. Februar 2025; das Bußgeld beträgt bis zu 35 Mio. € oder 7 % des weltweiten Jahresumsatzes. Wählen Sie alles, was zumindest annähernd zutrifft.",
    },
    options: [
      { value: "p_emotion_workplace", label: { en: "Detecting emotions of employees, candidates, or students (engagement, focus, exam stress, productivity)", de: "Erkennen von Emotionen bei Mitarbeitenden, Bewerbenden oder Lernenden (Engagement, Konzentration, Prüfungsstress, Produktivität)" }, cite: "Art. 5(1)(f)" },
      { value: "p_biometric_sensitive", label: { en: "Categorising people by race, political opinion, religion, sexual orientation, or trade-union membership using biometric data", de: "Kategorisierung von Personen nach Herkunft, politischer Meinung, Religion, sexueller Orientierung oder Gewerkschaftszugehörigkeit anhand biometrischer Daten" }, cite: "Art. 5(1)(g)" },
      { value: "p_social_scoring", label: { en: "Scoring people on social behaviour or personality, with effects in unrelated contexts", de: "Bewertung von Personen anhand von Sozialverhalten oder Persönlichkeitsmerkmalen mit Wirkung in unzusammenhängenden Kontexten" }, cite: "Art. 5(1)(c)" },
      { value: "p_facial_scrape", label: { en: "Building or expanding a facial-recognition database via untargeted scraping of the web or CCTV", de: "Aufbau oder Erweiterung einer Gesichtserkennungs-Datenbank durch ungezieltes Auslesen von Webinhalten oder Videoüberwachung" }, cite: "Art. 5(1)(e)" },
      { value: "p_predictive_crime", label: { en: "Predicting whether a specific person will commit a crime, based on profiling them", de: "Vorhersage einer Straftat einer bestimmten Person allein auf Basis von Profiling" }, cite: "Art. 5(1)(d)" },
      { value: "p_manipulation", label: { en: "Subliminal, manipulative, or deceptive techniques that distort behaviour and cause harm", de: "Unterschwellige, manipulative oder täuschende Techniken, die das Verhalten verzerren und schaden" }, cite: "Art. 5(1)(a)" },
      { value: "p_vulnerability", label: { en: "Exploiting vulnerabilities of children, the elderly, people with disabilities, or socioeconomically vulnerable groups", de: "Ausnutzung von Schwachstellen von Kindern, älteren Menschen, Menschen mit Behinderung oder sozioökonomisch schutzbedürftigen Gruppen" }, cite: "Art. 5(1)(b)" },
      { value: "p_realtime_biometric_le", label: { en: "Real-time remote biometric identification in public spaces by law enforcement", de: "Echtzeit-Fernidentifizierung in öffentlich zugänglichen Räumen durch Strafverfolgung" }, cite: "Art. 5(1)(h)" },
      { value: "none_prohibited", label: { en: "None of the above", de: "Keine davon" } },
    ],
  },
  {
    id: "annex",
    type: "multi",
    text: {
      en: "Do you use AI in any of these areas? Pick all that apply.",
      de: "Setzen Sie KI in einem dieser Bereiche ein? Mehrfachauswahl möglich.",
    },
    help: {
      en: "These are the eight high-risk use cases listed in Annex III. Pick everything that even arguably applies — the audit will narrow it down.",
      de: "Dies sind die acht Hochrisiko-Anwendungsfälle aus Anhang III. Wählen Sie alles, was zumindest näherungsweise zutrifft — das Audit grenzt anschließend ein.",
    },
    options: [
      { value: "hr", label: { en: "Recruitment, hiring, application screening, performance evaluation, promotion, termination, or task allocation", de: "Personalbeschaffung, Bewerber-Screening, Leistungsbewertung, Beförderung, Kündigung oder Aufgabenverteilung" }, cite: "Annex III §4" },
      { value: "credit", label: { en: "Credit scoring or creditworthiness evaluation (excluding pure fraud detection)", de: "Kreditscoring oder Bonitätsprüfung (ausgenommen reine Betrugserkennung)" }, cite: "Annex III §5(b)" },
      { value: "insurance", label: { en: "Risk assessment or pricing for life or health insurance", de: "Risikobewertung oder Tarifierung in Lebens- oder Krankenversicherung" }, cite: "Annex III §5(c)" },
      { value: "essential", label: { en: "Eligibility for public benefits, healthcare, emergency services, or 112 dispatch triage", de: "Anspruch auf öffentliche Leistungen, Gesundheitsversorgung, Notrufdienste oder 112-Triage" }, cite: "Annex III §5(a)(d)" },
      { value: "education", label: { en: "Education or vocational training — admissions, exam evaluation, exam supervision, level placement", de: "Bildung oder Berufsbildung — Zulassung, Prüfungsbewertung, Prüfungsaufsicht, Niveauzuweisung" }, cite: "Annex III §3" },
      { value: "infra", label: { en: "Safety component of critical infrastructure (energy, water, gas, road traffic, digital infrastructure)", de: "Sicherheitsbauteil in kritischer Infrastruktur (Strom, Wasser, Gas, Straßenverkehr, digitale Infrastruktur)" }, cite: "Annex III §2" },
      { value: "biometrics_remote", label: { en: "Remote biometric identification (one-to-many, not verification), or non-prohibited emotion / categorisation", de: "Biometrische Fernidentifizierung (1:n, keine Verifikation) oder nicht verbotene Emotions-/Kategorisierungs-Anwendungen" }, cite: "Annex III §1" },
      { value: "law", label: { en: "Law enforcement risk assessment, polygraph-equivalent tools, or evidence-reliability evaluation", de: "Strafverfolgung: Risikobewertung, Lügendetektor-ähnliche Tools oder Beweisbewertung" }, cite: "Annex III §6" },
      { value: "migration", label: { en: "Migration, asylum, border control, visa or asylum application assistance", de: "Migration, Asyl, Grenzkontrolle, Unterstützung bei Visa- oder Asylanträgen" }, cite: "Annex III §7" },
      { value: "justice", label: { en: "Judicial fact-finding / law-application assistance, or AI influencing election or voting behaviour", de: "Justiz: Sachverhalts-/Rechtsanwendungs-Unterstützung oder KI mit Einfluss auf Wahl- oder Abstimmungsverhalten" }, cite: "Annex III §8" },
      { value: "none_annex", label: { en: "None of the above", de: "Keine davon" } },
    ],
  },
  {
    id: "profiling",
    type: "single",
    text: {
      en: "Does the AI evaluate aspects of a person — their work performance, financial reliability, behaviour, location, or similar?",
      de: "Bewertet die KI Aspekte einer Person — Arbeitsleistung, Bonität, Verhalten, Standort oder Ähnliches?",
    },
    help: {
      en: "If yes, the AI is doing what GDPR and the AI Act call profiling. The Act is explicit: an Annex III system that profiles is high-risk by force of law — none of the Article 6(3) carve-outs can save you.",
      de: "Wenn ja, betreibt die KI das, was DSGVO und KI-Verordnung als Profiling bezeichnen. Die Verordnung ist eindeutig: Ein Anhang-III-System, das Profiling betreibt, ist kraft Gesetzes hochriskant — keine der Ausnahmen aus Artikel 6(3) greift dann.",
    },
    showIf: (a) =>
      Array.isArray(a.annex) && a.annex.length > 0 && !a.annex.includes("none_annex"),
    options: [
      { value: "yes", label: { en: "Yes — it evaluates aspects of identifiable people", de: "Ja — sie bewertet Aspekte identifizierbarer Personen" } },
      { value: "no", label: { en: "No — it doesn't evaluate aspects of individuals", de: "Nein — sie bewertet keine Personenaspekte" } },
      { value: "unsure", label: { en: "Not sure", de: "Unsicher" } },
    ],
  },
  {
    id: "carveout",
    type: "single",
    text: {
      en: "Which of these best describes how the AI is actually used?",
      de: "Welche Beschreibung trifft am ehesten zu?",
    },
    help: {
      en: "Article 6(3) lists four narrow exceptions where an Annex III system isn't high-risk — but only if it doesn't pose significant risk to health, safety, or fundamental rights. Pick the option that genuinely fits.",
      de: "Artikel 6(3) nennt vier eng gefasste Ausnahmen, in denen ein Anhang-III-System nicht hochriskant ist — vorausgesetzt, es stellt kein erhebliches Risiko für Gesundheit, Sicherheit oder Grundrechte dar. Wählen Sie die Option, die tatsächlich zutrifft.",
    },
    showIf: (a) =>
      Array.isArray(a.annex) &&
      a.annex.length > 0 &&
      !a.annex.includes("none_annex") &&
      a.profiling === "no",
    options: [
      { value: "decision", label: { en: "It makes the decision, or its output is the primary basis for the decision", de: "Sie trifft die Entscheidung oder ihre Ausgabe ist die primäre Entscheidungsgrundlage" } },
      { value: "narrow", label: { en: "It performs a narrow procedural task — formatting, parsing, routing", de: "Sie übernimmt eine eng umrissene verfahrenstechnische Aufgabe — Formatieren, Parsen, Weiterleiten" }, cite: "Art. 6(3)(a)" },
      { value: "improvement", label: { en: "It improves the result of a previously completed human activity", de: "Sie verbessert das Ergebnis einer zuvor abgeschlossenen menschlichen Tätigkeit" }, cite: "Art. 6(3)(b)" },
      { value: "pattern", label: { en: "It detects patterns or deviations to flag for human review, without replacing the human assessment", de: "Sie erkennt Muster oder Abweichungen zur Vorlage an Menschen, ersetzt aber nicht deren Bewertung" }, cite: "Art. 6(3)(c)" },
      { value: "preparatory", label: { en: "It performs a preparatory task before a human assessment", de: "Sie übernimmt eine vorbereitende Aufgabe vor einer menschlichen Bewertung" }, cite: "Art. 6(3)(d)" },
    ],
  },
  {
    id: "gpai",
    type: "single",
    text: { en: "How do you use general-purpose AI?", de: "Wie nutzen Sie KI mit allgemeinem Verwendungszweck?" },
    help: {
      en: "Article 50 transparency obligations attach to chatbots, deepfakes, emotion / biometric systems, and synthetic content. Articles 53 and 55 attach to providers who train or place GPAI models on the market — already in force since 2 August 2025.",
      de: "Die Transparenzpflichten aus Artikel 50 gelten für Chatbots, Deepfakes, Emotions-/Biometriesysteme und synthetische Inhalte. Artikel 53 und 55 gelten für Anbieter, die GPAI-Modelle trainieren oder auf den Markt bringen — bereits in Kraft seit 2. August 2025.",
    },
    options: [
      { value: "train_model", label: { en: "We train, fine-tune, or release our own foundation / general-purpose AI model", de: "Wir trainieren, feintunen oder veröffentlichen ein eigenes Foundation-/GPAI-Modell" } },
      { value: "synthetic_media", label: { en: "We generate synthetic image, audio, or video content for distribution", de: "Wir erzeugen synthetische Bild-, Audio- oder Videoinhalte zur Verbreitung" } },
      { value: "customer_chat", label: { en: "Customer-facing chatbot or AI in our product that users interact with", de: "Kundenseitiger Chatbot oder KI im Produkt, mit der Nutzende interagieren" } },
      { value: "internal_decisions", label: { en: "Internal — but it informs decisions about employees, candidates, or contracts", de: "Intern — fließt jedoch in Entscheidungen über Mitarbeitende, Bewerbende oder Verträge ein" } },
      { value: "internal_only", label: { en: "Internal only — drafting, summarising, code, research", de: "Nur intern — Entwürfe, Zusammenfassungen, Code, Recherche" } },
      { value: "none", label: { en: "We don't use general-purpose AI tools", de: "Wir nutzen keine GPAI-Tools" } },
    ],
  },
  {
    id: "describe",
    type: "text",
    text: { en: "In one sentence, describe how AI shows up in your business today.", de: "Beschreiben Sie in einem Satz, wo KI heute in Ihrem Unternehmen auftaucht." },
    help: { en: "Optional. Makes the 25-minute call far more useful.", de: "Optional. Macht das 25-Minuten-Gespräch deutlich produktiver." },
    placeholder: { en: "e.g. We use Copilot internally and a CV-screening tool from a vendor for hiring engineers.", de: "z. B. Wir nutzen Copilot intern und ein CV-Screening-Tool eines Anbieters für die Einstellung von Ingenieur:innen." },
    optional: true,
  },
];

// ---------- Article catalogue (codes are language-neutral; titles localised) ----------

const ART = {
  art4:  { code: "Art. 4",  title: { en: "AI literacy of staff using AI", de: "KI-Kompetenz der mit KI arbeitenden Mitarbeitenden" } },
  art5a: { code: "Art. 5(1)(a)", title: { en: "Subliminal / manipulative / deceptive techniques", de: "Unterschwellige / manipulative / täuschende Techniken" } },
  art5b: { code: "Art. 5(1)(b)", title: { en: "Exploiting vulnerabilities", de: "Ausnutzung von Schwachstellen" } },
  art5c: { code: "Art. 5(1)(c)", title: { en: "Social scoring", de: "Soziale Bewertung" } },
  art5d: { code: "Art. 5(1)(d)", title: { en: "Predictive criminal-risk profiling", de: "Vorhersagendes Kriminalitäts-Profiling" } },
  art5e: { code: "Art. 5(1)(e)", title: { en: "Untargeted facial-recognition database scraping", de: "Ungezieltes Auslesen für Gesichtserkennungs-Datenbanken" } },
  art5f: { code: "Art. 5(1)(f)", title: { en: "Workplace / education emotion recognition", de: "Emotionserkennung am Arbeitsplatz / in Bildung" } },
  art5g: { code: "Art. 5(1)(g)", title: { en: "Biometric categorisation by sensitive attributes", de: "Biometrische Kategorisierung nach sensiblen Merkmalen" } },
  art5h: { code: "Art. 5(1)(h)", title: { en: "Real-time remote biometric ID by law enforcement", de: "Echtzeit-Fernidentifizierung durch Strafverfolgung" } },
  art6:  { code: "Art. 6",  title: { en: "Classification rules for high-risk AI", de: "Einstufungsregeln für Hochrisiko-KI" } },
  art8:  { code: "Art. 8",  title: { en: "Compliance with Section 2 high-risk requirements", de: "Erfüllung der Hochrisiko-Anforderungen aus Abschnitt 2" } },
  art9:  { code: "Art. 9",  title: { en: "Risk-management system", de: "Risikomanagementsystem" } },
  art10: { code: "Art. 10", title: { en: "Data and data governance", de: "Daten und Daten-Governance" } },
  art11: { code: "Art. 11", title: { en: "Technical documentation", de: "Technische Dokumentation" } },
  art12: { code: "Art. 12", title: { en: "Record-keeping (logging)", de: "Aufzeichnungspflichten (Logging)" } },
  art13: { code: "Art. 13", title: { en: "Transparency to deployers", de: "Transparenz gegenüber Betreibern" } },
  art14: { code: "Art. 14", title: { en: "Human oversight", de: "Menschliche Aufsicht" } },
  art15: { code: "Art. 15", title: { en: "Accuracy, robustness, cybersecurity", de: "Genauigkeit, Robustheit, Cybersicherheit" } },
  art16: { code: "Art. 16", title: { en: "Provider obligations for high-risk AI", de: "Anbieterpflichten bei Hochrisiko-KI" } },
  art17: { code: "Art. 17", title: { en: "Quality management system", de: "Qualitätsmanagementsystem" } },
  art26: { code: "Art. 26", title: { en: "Deployer obligations for high-risk AI", de: "Betreiberpflichten bei Hochrisiko-KI" } },
  art27: { code: "Art. 27", title: { en: "Fundamental Rights Impact Assessment (FRIA)", de: "Grundrechte-Folgenabschätzung (FRIA)" } },
  art43: { code: "Art. 43", title: { en: "Conformity assessment", de: "Konformitätsbewertung" } },
  art47: { code: "Art. 47", title: { en: "EU declaration of conformity", de: "EU-Konformitätserklärung" } },
  art48: { code: "Art. 48", title: { en: "CE marking", de: "CE-Kennzeichnung" } },
  art49: { code: "Art. 49", title: { en: "Registration in the EU database", de: "Registrierung in der EU-Datenbank" } },
  art50_1: { code: "Art. 50(1)", title: { en: "Disclose to users they're interacting with AI (provider)", de: "Hinweis an Nutzende auf KI-Interaktion (Anbieter)" } },
  art50_2: { code: "Art. 50(2)", title: { en: "Mark AI-generated synthetic content (provider)", de: "Kennzeichnung KI-generierter synthetischer Inhalte (Anbieter)" } },
  art50_3: { code: "Art. 50(3)", title: { en: "Inform people exposed to emotion / biometric systems (deployer)", de: "Information bei Emotions-/Biometriesystemen (Betreiber)" } },
  art50_4: { code: "Art. 50(4)", title: { en: "Disclose deepfakes (deployer)", de: "Deepfakes offenlegen (Betreiber)" } },
  art53: { code: "Art. 53", title: { en: "GPAI model provider obligations", de: "Pflichten der GPAI-Modellanbieter" } },
  art55: { code: "Art. 55", title: { en: "GPAI model with systemic risk", de: "GPAI-Modelle mit systemischem Risiko" } },
  art99: { code: "Art. 99", title: { en: "Penalties (up to €35M / 7%, €15M / 3%, €7.5M / 1%)", de: "Bußgelder (bis 35 Mio. €/7 %, 15 Mio. €/3 %, 7,5 Mio. €/1 %)" } },
  art113: { code: "Art. 113", title: { en: "Phased application timeline", de: "Gestaffelter Anwendungszeitplan" } },
};

const PROHIBITED_TO_ARTICLE = {
  p_manipulation: ART.art5a,
  p_vulnerability: ART.art5b,
  p_social_scoring: ART.art5c,
  p_predictive_crime: ART.art5d,
  p_facial_scrape: ART.art5e,
  p_emotion_workplace: ART.art5f,
  p_biometric_sensitive: ART.art5g,
  p_realtime_biometric_le: ART.art5h,
};

const PROHIBITED_LABEL = {
  p_emotion_workplace: { en: "Workplace / education emotion recognition", de: "Emotionserkennung am Arbeitsplatz / in Bildung" },
  p_biometric_sensitive: { en: "Biometric categorisation by sensitive attributes", de: "Biometrische Kategorisierung nach sensiblen Merkmalen" },
  p_social_scoring: { en: "Social scoring", de: "Soziale Bewertung" },
  p_facial_scrape: { en: "Untargeted facial-recognition scraping", de: "Ungezieltes Auslesen für Gesichtserkennung" },
  p_predictive_crime: { en: "Predictive criminal-risk profiling", de: "Vorhersagendes Kriminalitäts-Profiling" },
  p_manipulation: { en: "Subliminal / manipulative techniques", de: "Unterschwellige / manipulative Techniken" },
  p_vulnerability: { en: "Exploitation of vulnerabilities", de: "Ausnutzung von Schwachstellen" },
  p_realtime_biometric_le: { en: "Real-time biometric ID (law enforcement)", de: "Echtzeit-Fernidentifizierung (Strafverfolgung)" },
};

const ANNEX_LABELS = {
  hr: { en: "Annex III §4 — Employment & worker management", de: "Anhang III §4 — Beschäftigung & Personalmanagement" },
  credit: { en: "Annex III §5(b) — Creditworthiness & credit scoring", de: "Anhang III §5(b) — Bonität & Kreditscoring" },
  insurance: { en: "Annex III §5(c) — Life & health insurance risk and pricing", de: "Anhang III §5(c) — Lebens- und Krankenversicherung: Risiko & Tarif" },
  essential: { en: "Annex III §5(a),(d) — Essential services & emergency dispatch", de: "Anhang III §5(a),(d) — Daseinsvorsorge & Notrufdienste" },
  education: { en: "Annex III §3 — Education & vocational training", de: "Anhang III §3 — Bildung & Berufsbildung" },
  infra: { en: "Annex III §2 — Critical infrastructure", de: "Anhang III §2 — Kritische Infrastruktur" },
  biometrics_remote: { en: "Annex III §1 — Biometrics (remote ID, non-prohibited)", de: "Anhang III §1 — Biometrie (Fernidentifizierung, nicht verboten)" },
  law: { en: "Annex III §6 — Law enforcement", de: "Anhang III §6 — Strafverfolgung" },
  migration: { en: "Annex III §7 — Migration, asylum, border control", de: "Anhang III §7 — Migration, Asyl, Grenzkontrolle" },
  justice: { en: "Annex III §8 — Justice & democratic processes", de: "Anhang III §8 — Justiz & demokratische Prozesse" },
};

const FRIA_TRIGGER = new Set(["credit", "insurance", "essential", "law", "migration", "justice"]);

const CARVEOUT_LABELS = {
  narrow: { en: "Article 6(3)(a) — narrow procedural task", de: "Artikel 6(3)(a) — eng umrissene verfahrenstechnische Aufgabe" },
  improvement: { en: "Article 6(3)(b) — improvement of a completed human activity", de: "Artikel 6(3)(b) — Verbesserung einer abgeschlossenen menschlichen Tätigkeit" },
  pattern: { en: "Article 6(3)(c) — pattern detection without replacing human review", de: "Artikel 6(3)(c) — Mustererkennung ohne Ersatz der menschlichen Bewertung" },
  preparatory: { en: "Article 6(3)(d) — preparatory task before a human assessment", de: "Artikel 6(3)(d) — vorbereitende Aufgabe vor einer menschlichen Bewertung" },
};

// ---------- Result text builders (per language) ----------

const TEXT = {
  prohibited: {
    headline: { en: "This is prohibited under Article 5", de: "Dies ist nach Artikel 5 verboten" },
    summary: {
      en: "One or more of the practices you flagged is banned outright by Article 5 of the AI Act. These prohibitions have applied since 2 February 2025. The Act doesn't ask for risk-management or documentation — it requires you to stop. Penalties under Article 99(3) reach €35M or 7% of global turnover, and SMEs are not exempt from the prohibition itself.",
      de: "Mindestens eine der von Ihnen markierten Praktiken ist nach Artikel 5 der KI-Verordnung ausdrücklich verboten. Die Verbote gelten seit 2. Februar 2025. Die Verordnung verlangt hier kein Risikomanagement und keine Dokumentation — sie verlangt das Einstellen der Praxis. Bußgelder nach Artikel 99(3) reichen bis 35 Mio. € oder 7 % des weltweiten Umsatzes; vom Verbot selbst sind KMU nicht ausgenommen.",
    },
    disclaimer: {
      en: "This is the strongest signal an automated tool can give. Do not interpret it as legal advice — but do treat it as a reason to talk to a lawyer this week, not next quarter.",
      de: "Stärker kann ein automatisiertes Tool nicht warnen. Das ersetzt keine Rechtsberatung — aber es ist Grund genug, diese Woche mit einer Kanzlei zu sprechen, nicht im nächsten Quartal.",
    },
  },
  out_of_scope: {
    headline: { en: "AI Act likely doesn't apply to you today", de: "Die KI-Verordnung gilt für Sie aktuell vermutlich nicht" },
    summary: {
      en: "Article 2 makes the Act apply to providers placing AI on the EU market, deployers established in the EU, and providers / deployers whose AI output is used in the EU. Based on your answers, none of those triggers fire. Worth re-checking the moment you sign your first EU customer or your AI's output reaches an EU user.",
      de: "Artikel 2 erfasst Anbieter, die KI auf dem EU-Markt anbieten, in der EU niedergelassene Betreiber sowie Anbieter und Betreiber, deren KI-Ausgabe in der EU verwendet wird. Auf Basis Ihrer Antworten greift keiner dieser Auslöser. Erneut zu prüfen, sobald der erste EU-Kunde unterschreibt oder Ihre KI-Ausgabe einen EU-Nutzer erreicht.",
    },
  },
  minimal_no_ai: {
    headline: { en: "Outside scope today — but Article 4 starts on day one", de: "Heute außerhalb des Anwendungsbereichs — Artikel 4 gilt jedoch ab Tag eins" },
    summary: {
      en: "You don't use or build AI in scope of the Act today. The moment you bring in a vendor that runs AI on your data, or roll out Copilot or ChatGPT for the team, deployer obligations attach. The Article 4 AI-literacy duty applies from day one — a short internal AI policy and a one-pager for staff is usually enough at this stage.",
      de: "Sie nutzen oder entwickeln derzeit keine KI im Anwendungsbereich der Verordnung. Sobald ein Anbieter mit KI auf Ihren Daten arbeitet oder Sie Copilot bzw. ChatGPT teamweit einführen, greifen Betreiberpflichten. Die KI-Kompetenzpflicht aus Artikel 4 gilt ab Tag eins — eine kurze interne KI-Richtlinie und ein Onepager fürs Team reichen in dieser Phase meist aus.",
    },
  },
  high: {
    headline: { en: "High-risk under Annex III", de: "Hochrisiko nach Anhang III" },
    reason_profiling: {
      en: "the AI processes personal data to evaluate aspects of a person, and Article 6(3) explicitly cannot save profiling systems from high-risk classification",
      de: "die KI verarbeitet personenbezogene Daten zur Bewertung von Personen, und Artikel 6(3) kann Profiling-Systeme ausdrücklich nicht von der Hochrisiko-Einstufung ausnehmen",
    },
    reason_profiling_unsure: {
      en: "you weren't sure whether the AI profiles people — the audit defaults to the conservative classification, since Article 6(3) cannot save a profiling system",
      de: "Sie waren unsicher, ob die KI Profiling betreibt — das Audit wählt die konservative Einstufung, denn Artikel 6(3) entlastet ein Profiling-System nicht",
    },
    reason_decision: {
      en: "the AI is the decision-maker (or its output is the primary basis for the decision), so no Article 6(3) carve-out applies",
      de: "die KI trifft die Entscheidung (oder ihre Ausgabe ist die primäre Entscheidungsgrundlage), weshalb keine Ausnahme nach Artikel 6(3) greift",
    },
    obl_both: {
      en: "You're both provider and deployer, so Articles 16–17 (provider obligations + QMS) and Article 26 (deployer obligations) both apply.",
      de: "Sie sind sowohl Anbieter als auch Betreiber — daher gelten sowohl die Artikel 16–17 (Anbieterpflichten + QM-System) als auch Artikel 26 (Betreiberpflichten).",
    },
    obl_provider: {
      en: "As a provider you carry Articles 16–17 (provider obligations + QMS), Article 43 (conformity assessment), Article 47 (declaration of conformity), Article 48 (CE marking), and Article 49 (registration in the EU database).",
      de: "Als Anbieter tragen Sie Artikel 16–17 (Anbieterpflichten + QM-System), Artikel 43 (Konformitätsbewertung), Artikel 47 (Konformitätserklärung), Artikel 48 (CE-Kennzeichnung) sowie Artikel 49 (Registrierung in der EU-Datenbank).",
    },
    obl_deployer: {
      en: "As a deployer you carry Article 26: human oversight, monitoring, logging, informing affected workers / customers, and cooperating with authorities.",
      de: "Als Betreiber tragen Sie Artikel 26: menschliche Aufsicht, Monitoring, Logging, Information betroffener Beschäftigter / Kund:innen sowie Kooperation mit Behörden.",
    },
    fria: {
      en: " Article 27 also requires a Fundamental Rights Impact Assessment for this category.",
      de: " Artikel 27 verlangt für diese Kategorie zusätzlich eine Grundrechte-Folgenabschätzung.",
    },
    tail: {
      en: " Annex III obligations apply from 2 August 2026 — the full lifecycle (risk-management system, technical documentation, post-market monitoring) takes months, not weeks.",
      de: " Die Anhang-III-Pflichten gelten ab 2. August 2026 — der vollständige Lebenszyklus (Risikomanagement, technische Dokumentation, Beobachtung nach Inverkehrbringen) dauert Monate, nicht Wochen.",
    },
    summary_prefix: { en: "Your use is high-risk because ", de: "Ihre Anwendung ist hochriskant, weil " },
  },
  possibly_high_carveout: {
    headline: { en: "Possibly high-risk — Article 6(3) may carve you out", de: "Möglicherweise hochriskant — Artikel 6(3) könnte Sie ausnehmen" },
    summary_template: {
      en: "You're in an Annex III area but the use you described maps to {carveout}. The carve-out only holds if (a) you can document why it applies, AND (b) the system does not pose significant risk to health, safety, or fundamental rights. The provider must register the carve-out reasoning in the EU database before placing the system on the market. A misclassified \"carved-out\" system is treated as an undeclared high-risk system at audit time — Article 99(4) penalties up to €15M or 3% of turnover.",
      de: "Sie liegen in einem Anhang-III-Bereich, aber Ihre Nutzung passt auf {carveout}. Die Ausnahme greift nur, wenn (a) Sie die Anwendung der Ausnahme dokumentieren können UND (b) das System kein erhebliches Risiko für Gesundheit, Sicherheit oder Grundrechte darstellt. Der Anbieter muss die Begründung vor Markteinführung in der EU-Datenbank registrieren. Ein falsch als „ausgenommen“ eingestuftes System wird im Audit wie ein nicht angemeldetes Hochrisiko-System behandelt — Bußgeld nach Artikel 99(4) bis zu 15 Mio. € oder 3 % des Umsatzes.",
    },
  },
  gpai_provider: {
    headline: { en: "GPAI provider obligations apply", de: "GPAI-Anbieterpflichten gelten" },
    summary: {
      en: "You train, fine-tune, or release a general-purpose AI model on the EU market. Article 53 requires technical documentation of the model, a copyright-compliance policy, and a public summary of the training data. If your model meets the systemic-risk threshold (Article 51), Article 55 adds model evaluation, adversarial testing, incident reporting, and cybersecurity duties on top. These obligations are already in force since 2 August 2025.",
      de: "Sie trainieren, feintunen oder veröffentlichen ein KI-Modell mit allgemeinem Verwendungszweck auf dem EU-Markt. Artikel 53 verlangt technische Dokumentation des Modells, eine Urheberrechts-Compliance-Richtlinie und eine öffentliche Zusammenfassung der Trainingsdaten. Erreicht Ihr Modell die Schwelle systemischen Risikos (Artikel 51), kommen nach Artikel 55 Modellbewertung, adversariale Tests, Vorfallsmeldungen und Cybersicherheitspflichten hinzu. Diese Pflichten gelten bereits seit 2. August 2025.",
    },
  },
  limited_synthetic: {
    headline: { en: "Limited risk — synthetic content must be labelled", de: "Begrenztes Risiko — synthetische Inhalte sind zu kennzeichnen" },
    summary: {
      en: "You distribute AI-generated images, audio, or video. As a deployer you must disclose deepfakes (Art. 50(4)). If you also build the model, providers must mark outputs as synthetic in a machine-readable way (Art. 50(2)). The artistic / satirical / fictional exception only requires disclosing the existence of the manipulation, not labelling each output. Article 4 AI literacy applies in either case.",
      de: "Sie verbreiten KI-generierte Bilder, Audio- oder Videoinhalte. Als Betreiber müssen Sie Deepfakes offenlegen (Art. 50(4)). Bauen Sie das Modell zudem selbst, müssen Anbieter die Ausgaben maschinenlesbar als synthetisch kennzeichnen (Art. 50(2)). Die Ausnahme für Kunst, Satire, Fiktion verlangt lediglich Hinweis auf die Manipulation an sich, keine Einzelkennzeichnung. Artikel 4 KI-Kompetenz gilt in jedem Fall.",
    },
  },
  limited_chat: {
    headline: { en: "Limited risk — Article 50 transparency applies", de: "Begrenztes Risiko — Transparenzpflicht aus Artikel 50" },
    summary: {
      en: "Your product interacts with users via AI. Article 50(1) requires that users are told they're interacting with AI, unless that's obvious from context. If your product also generates synthetic text, image, audio, or video, Article 50(2) requires those outputs are machine-readably marked. Article 4 AI-literacy duty applies to staff operating the system.",
      de: "Ihr Produkt interagiert über KI mit Nutzenden. Artikel 50(1) verlangt einen Hinweis auf die KI-Interaktion, sofern das aus dem Kontext nicht ohnehin klar ist. Erzeugt Ihr Produkt zudem synthetischen Text, Bild, Audio oder Video, sind diese Ausgaben nach Artikel 50(2) maschinenlesbar zu kennzeichnen. Die KI-Kompetenzpflicht nach Artikel 4 gilt für die Bedienenden.",
    },
  },
  borderline_internal_decisions: {
    headline: { en: "Borderline — watch the line into Annex III §4", de: "Grenzfall — Übergang zu Anhang III §4 im Auge behalten" },
    summary: {
      en: "Using a general-purpose model to support employment, candidate, or contract decisions sits exactly on the §4 boundary. The line is whether the AI is the basis for the decision (high-risk) or one input a human reviews independently (Article 6(3)(b)/(d) carve-outs may apply, profiling override permitting). Document who owns the decision and how the human reviewer is positioned — that documentation is the difference between a clean Article 26 deployer file and an undeclared high-risk system.",
      de: "Ein GPAI-Modell für Personal-, Bewerber- oder Vertragsentscheidungen sitzt genau auf der §4-Grenze. Maßgeblich ist, ob die KI Entscheidungsgrundlage ist (hochriskant) oder nur eine Eingabe für eine eigenständige menschliche Bewertung (dann ggf. Ausnahmen nach Artikel 6(3)(b)/(d), sofern kein Profiling). Dokumentieren Sie, wer entscheidet und wie der menschliche Reviewer eingebunden ist — diese Dokumentation entscheidet zwischen sauberer Artikel-26-Akte und nicht angemeldetem Hochrisiko-System.",
    },
  },
  minimal_internal_only: {
    headline: { en: "Minimal risk — Article 4 still applies", de: "Minimales Risiko — Artikel 4 gilt dennoch" },
    summary: {
      en: "Internal-only use of Copilot, ChatGPT, Claude, or Gemini for drafting, summarising, code, or research is minimal-risk under the Act. Article 4 requires staff to have a sufficient level of AI literacy proportionate to the use — usually a one-page internal AI policy, a list of approved tools, and a do-not-paste list of data categories.",
      de: "Rein interne Nutzung von Copilot, ChatGPT, Claude oder Gemini für Entwürfe, Zusammenfassungen, Code oder Recherche ist minimales Risiko. Artikel 4 verlangt von Mitarbeitenden ein der Nutzung angemessenes Niveau an KI-Kompetenz — üblich sind eine einseitige interne Richtlinie, eine Liste freigegebener Tools und eine „Bitte nicht einfügen“-Liste für Datenkategorien.",
    },
  },
  fallback: {
    headline: { en: "Worth a closer look", de: "Genaueres Hinsehen lohnt sich" },
    summary: {
      en: "Your answers don't cleanly map to a single risk class. The most useful next step is a 25-minute call to walk through how AI actually shows up in your business — that's where the real classification happens.",
      de: "Ihre Antworten lassen sich nicht eindeutig einer Risikoklasse zuordnen. Der nächste sinnvolle Schritt ist ein 25-Minuten-Gespräch über die tatsächliche KI-Nutzung in Ihrem Unternehmen — dort wird die Einstufung belastbar.",
    },
  },
  add_on_chat: {
    en: "Article 50(1) transparency also applies — your product must tell users they're interacting with AI.",
    de: "Außerdem greift die Transparenzpflicht aus Artikel 50(1) — Ihr Produkt muss Nutzende auf die KI-Interaktion hinweisen.",
  },
  add_on_synthetic: {
    en: "Article 50(4) deepfake disclosure also applies. If you build the model, Article 50(2) machine-readable marking applies too.",
    de: "Außerdem greift die Deepfake-Offenlegung aus Artikel 50(4). Wer das Modell selbst baut, muss zusätzlich nach Artikel 50(2) maschinenlesbar kennzeichnen.",
  },
  add_on_gpai: {
    en: "Article 53 GPAI provider obligations stack on top — technical docs, copyright policy, training-data summary, already in force since 2 August 2025.",
    de: "Zusätzlich gelten die GPAI-Anbieterpflichten aus Artikel 53 — technische Dokumentation, Urheberrechts-Richtlinie, Zusammenfassung der Trainingsdaten, bereits seit 2. August 2025 in Kraft.",
  },
  // Deadline labels
  dl_in_force_art5: {
    en: "Already in force since 2 February 2025",
    de: "Bereits in Kraft seit 2. Februar 2025",
  },
  dl_in_force_gpai: {
    en: "Already in force since 2 August 2025",
    de: "Bereits in Kraft seit 2. August 2025",
  },
  dl_annex_iii: {
    en: "Annex III obligations apply 2 August 2026",
    de: "Anhang-III-Pflichten gelten ab 2. August 2026",
  },
  dl_art50: {
    en: "Art. 50 transparency applies 2 August 2026",
    de: "Transparenzpflichten Art. 50 gelten ab 2. August 2026",
  },
};

// ---------- Pack preview (what the €299/mo subscription delivers) ----------

export const PACKS = {
  high: {
    en: {
      title: "High-risk Compliance Pack",
      items: [
        "AI Inventory Register — lawyer-reviewed template",
        "Annex III risk assessment per system",
        "Article 9 risk-management plan",
        "Article 26 deployer obligations file (human oversight, logging, monitoring)",
        "Transparency notices for affected workers / customers",
        "AI Governance Policy + Employee AI Usage Guidelines",
        "Article 27 FRIA template (where applicable)",
        "Quarterly regulatory updates",
      ],
    },
    de: {
      title: "Compliance-Paket Hochrisiko",
      items: [
        "KI-Bestandsliste — anwaltlich geprüfte Vorlage",
        "Anhang-III-Risikobewertung je System",
        "Risikomanagementplan nach Artikel 9",
        "Betreiber-Akte nach Artikel 26 (Aufsicht, Logging, Monitoring)",
        "Transparenzhinweise für betroffene Beschäftigte / Kund:innen",
        "KI-Governance-Richtlinie + Mitarbeitenden-Leitlinien",
        "FRIA-Vorlage nach Artikel 27 (sofern einschlägig)",
        "Quartalsweise Regulatorik-Updates",
      ],
    },
  },
  possibly_high: {
    en: {
      title: "Possibly-high-risk Pack",
      items: [
        "AI Inventory Register — lawyer-reviewed template",
        "Article 6(3) carve-out documentation file",
        "EU-database registration support",
        "Transparency notices template",
        "AI Governance Policy + Employee AI Usage Guidelines",
        "Quarterly regulatory updates",
      ],
    },
    de: {
      title: "Paket „möglicherweise hochriskant“",
      items: [
        "KI-Bestandsliste — anwaltlich geprüfte Vorlage",
        "Dokumentation der Ausnahme nach Artikel 6(3)",
        "Unterstützung bei der EU-Datenbank-Registrierung",
        "Vorlage Transparenzhinweise",
        "KI-Governance-Richtlinie + Mitarbeitenden-Leitlinien",
        "Quartalsweise Regulatorik-Updates",
      ],
    },
  },
  gpai_provider: {
    en: {
      title: "GPAI Provider Pack",
      items: [
        "Article 53 technical documentation template",
        "Copyright-compliance policy",
        "Training-data summary (Annex XI / XII)",
        "Article 55 systemic-risk evaluation framework (where applicable)",
        "Quarterly regulatory updates",
      ],
    },
    de: {
      title: "GPAI-Anbieter-Paket",
      items: [
        "Vorlage technische Dokumentation nach Artikel 53",
        "Urheberrechts-Compliance-Richtlinie",
        "Zusammenfassung der Trainingsdaten (Anhang XI / XII)",
        "Bewertungsrahmen für systemisches Risiko nach Artikel 55 (sofern einschlägig)",
        "Quartalsweise Regulatorik-Updates",
      ],
    },
  },
  limited: {
    en: {
      title: "Limited-risk Pack",
      items: [
        "Article 50 transparency disclosures (chatbot / synthetic content)",
        "Deepfake disclosure templates",
        "Employee AI Usage Guidelines",
        "Quarterly regulatory updates",
      ],
    },
    de: {
      title: "Paket „begrenztes Risiko“",
      items: [
        "Transparenzhinweise nach Artikel 50 (Chatbot / synthetische Inhalte)",
        "Vorlagen Deepfake-Offenlegung",
        "Mitarbeitenden-Leitlinien KI-Nutzung",
        "Quartalsweise Regulatorik-Updates",
      ],
    },
  },
  minimal: {
    en: {
      title: "Minimal-risk Pack",
      items: [
        "Internal AI Usage Policy template",
        "Article 4 AI-literacy one-pager for staff",
        "Approved-tools and do-not-paste list",
        "Quarterly regulatory updates",
      ],
    },
    de: {
      title: "Paket „minimales Risiko“",
      items: [
        "Vorlage interne KI-Richtlinie",
        "Onepager KI-Kompetenz nach Artikel 4 fürs Team",
        "Liste freigegebener Tools und „Bitte nicht einfügen“-Liste",
        "Quartalsweise Regulatorik-Updates",
      ],
    },
  },
};

// ---------- Helpers ----------

const realPicks = (arr, none) => (Array.isArray(arr) ? arr.filter((x) => x !== none) : []);

function localiseArticleList(list) {
  return list.map((a) => ({ code: a.code, title: a.title }));
}

function deadline(kind) {
  if (kind === "art5_active") return { date: DATE_ART5_PROHIBITIONS, label: TEXT.dl_in_force_art5, phase: "active" };
  if (kind === "gpai_active") return { date: DATE_GPAI_PROVIDER, label: TEXT.dl_in_force_gpai, phase: "active" };
  if (kind === "annex_iii") return { date: DATE_ANNEX_III, label: TEXT.dl_annex_iii, phase: "upcoming" };
  if (kind === "art50") return { date: DATE_ANNEX_III, label: TEXT.dl_art50, phase: "upcoming" };
  return null;
}

// ---------- Classifier ----------

export function classify(answers) {
  const country = answers.country;
  const role = answers.role;
  const prohibited = realPicks(answers.prohibited, "none_prohibited");
  const annex = realPicks(answers.annex, "none_annex");
  const profiling = answers.profiling;
  const carveout = answers.carveout;
  const gpai = answers.gpai;

  // RULE 1 — Art. 5 prohibited practices override everything.
  if (prohibited.length > 0) {
    const triggers = prohibited.map((p) => ({
      label: PROHIBITED_LABEL[p],
      article: PROHIBITED_TO_ARTICLE[p],
    }));
    return {
      tier: "prohibited",
      packKey: null,
      headline: TEXT.prohibited.headline,
      summary: TEXT.prohibited.summary,
      annexAreas: [],
      articles: localiseArticleList([...triggers.map((t) => t.article).filter(Boolean), ART.art99]),
      prohibitedTriggers: triggers,
      deadline: deadline("art5_active"),
      nextStep: "stop",
      urgency: "critical",
      disclaimer: TEXT.prohibited.disclaimer,
    };
  }

  // RULE 2 — out of scope.
  if (country === "non_eu_no_eu" && role !== "provider" && role !== "both") {
    return {
      tier: "out_of_scope",
      packKey: null,
      headline: TEXT.out_of_scope.headline,
      summary: TEXT.out_of_scope.summary,
      annexAreas: [],
      articles: [],
      deadline: null,
      nextStep: "soft",
      urgency: "low",
    };
  }

  // RULE 3 — no AI use at all.
  if (role === "none" && (gpai === "none" || !gpai)) {
    return {
      tier: "minimal",
      packKey: "minimal",
      headline: TEXT.minimal_no_ai.headline,
      summary: TEXT.minimal_no_ai.summary,
      annexAreas: [],
      articles: localiseArticleList([ART.art4]),
      deadline: deadline("art5_active"),
      nextStep: "soft",
      urgency: "low",
    };
  }

  const isProvider = role === "provider" || role === "both";
  const isDeployer = role === "deployer" || role === "both";
  const annexLabels = annex.map((a) => ANNEX_LABELS[a]).filter(Boolean);
  const friaApplies = annex.some((a) => FRIA_TRIGGER.has(a));

  // RULE 4 — high-risk by profiling override.
  if (annex.length > 0 && profiling === "yes") {
    return buildHighRisk({ annexLabels, isProvider, isDeployer, friaApplies, reason: "profiling", gpai });
  }

  // RULE 5 — Annex III + decision-maker = high-risk.
  if (annex.length > 0 && profiling === "no" && carveout === "decision") {
    return buildHighRisk({ annexLabels, isProvider, isDeployer, friaApplies, reason: "decision", gpai });
  }

  // RULE 6 — 6(3) carve-out claimed → possibly high-risk.
  if (annex.length > 0 && profiling === "no" && carveout && carveout !== "decision") {
    const carveoutLabel = CARVEOUT_LABELS[carveout];
    const summary = {
      en: TEXT.possibly_high_carveout.summary_template.en.replace("{carveout}", carveoutLabel.en),
      de: TEXT.possibly_high_carveout.summary_template.de.replace("{carveout}", carveoutLabel.de),
    };
    return {
      tier: "possibly_high",
      packKey: "possibly_high",
      headline: TEXT.possibly_high_carveout.headline,
      summary,
      annexAreas: annexLabels,
      articles: localiseArticleList([ART.art6, ART.art4, ART.art49]),
      carveoutReason: carveoutLabel,
      deadline: deadline("annex_iii"),
      nextStep: "strong",
      urgency: "medium",
      ...gpaiAddOnRich(gpai),
    };
  }

  // RULE 7 — Annex III + profiling unsure → conservative high-risk.
  if (annex.length > 0 && profiling === "unsure") {
    return buildHighRisk({ annexLabels, isProvider, isDeployer, friaApplies, reason: "profiling_unsure", gpai });
  }

  // RULE 8 — GPAI provider.
  if (gpai === "train_model") {
    return {
      tier: "gpai_provider",
      packKey: "gpai_provider",
      headline: TEXT.gpai_provider.headline,
      summary: TEXT.gpai_provider.summary,
      annexAreas: [],
      articles: localiseArticleList([ART.art53, ART.art55, ART.art4]),
      deadline: deadline("gpai_active"),
      nextStep: "strong",
      urgency: "high",
    };
  }

  // RULE 9 — synthetic media generation.
  if (gpai === "synthetic_media") {
    return {
      tier: "limited",
      packKey: "limited",
      headline: TEXT.limited_synthetic.headline,
      summary: TEXT.limited_synthetic.summary,
      annexAreas: [],
      articles: localiseArticleList([ART.art50_4, ART.art50_2, ART.art4]),
      deadline: deadline("art50"),
      nextStep: "soft",
      urgency: "medium",
    };
  }

  // RULE 10 — customer-facing chat.
  if (gpai === "customer_chat") {
    const articles = [ART.art50_1, ART.art4];
    if (isProvider) articles.unshift(ART.art50_2);
    return {
      tier: "limited",
      packKey: "limited",
      headline: TEXT.limited_chat.headline,
      summary: TEXT.limited_chat.summary,
      annexAreas: [],
      articles: localiseArticleList(articles),
      deadline: deadline("art50"),
      nextStep: "soft",
      urgency: "medium",
    };
  }

  // RULE 11 — internal-decisions GPAI = §4 borderline.
  if (gpai === "internal_decisions") {
    return {
      tier: "possibly_high",
      packKey: "possibly_high",
      headline: TEXT.borderline_internal_decisions.headline,
      summary: TEXT.borderline_internal_decisions.summary,
      annexAreas: [],
      articles: localiseArticleList([ART.art6, ART.art26, ART.art4]),
      deadline: deadline("annex_iii"),
      nextStep: "strong",
      urgency: "medium",
    };
  }

  // RULE 12 — internal-only GPAI.
  if (gpai === "internal_only") {
    return {
      tier: "minimal",
      packKey: "minimal",
      headline: TEXT.minimal_internal_only.headline,
      summary: TEXT.minimal_internal_only.summary,
      annexAreas: [],
      articles: localiseArticleList([ART.art4]),
      deadline: deadline("art5_active"),
      nextStep: "soft",
      urgency: "low",
    };
  }

  // FALLBACK
  return {
    tier: "limited",
    packKey: "limited",
    headline: TEXT.fallback.headline,
    summary: TEXT.fallback.summary,
    annexAreas: annexLabels,
    articles: localiseArticleList([ART.art4]),
    deadline: null,
    nextStep: "strong",
    urgency: "medium",
  };
}

function buildHighRisk({ annexLabels, isProvider, isDeployer, friaApplies, reason, gpai }) {
  const articles = [ART.art6, ART.art8, ART.art9, ART.art10, ART.art11, ART.art12, ART.art14, ART.art15];
  if (isProvider) articles.push(ART.art13, ART.art16, ART.art17, ART.art43, ART.art47, ART.art48, ART.art49);
  if (isDeployer) articles.push(ART.art26);
  if (friaApplies) articles.push(ART.art27);
  articles.push(ART.art4);

  const reasonText = TEXT.high[`reason_${reason}`];
  const obligationsLine = isProvider && isDeployer
    ? TEXT.high.obl_both
    : isProvider
    ? TEXT.high.obl_provider
    : TEXT.high.obl_deployer;
  const friaLine = friaApplies ? TEXT.high.fria : { en: "", de: "" };

  const summary = {
    en: `${TEXT.high.summary_prefix.en}${reasonText.en}. ${obligationsLine.en}${friaLine.en}${TEXT.high.tail.en}`,
    de: `${TEXT.high.summary_prefix.de}${reasonText.de}. ${obligationsLine.de}${friaLine.de}${TEXT.high.tail.de}`,
  };

  return {
    tier: "high",
    packKey: "high",
    headline: TEXT.high.headline,
    summary,
    annexAreas: annexLabels,
    articles: localiseArticleList(articles),
    deadline: deadline("annex_iii"),
    nextStep: "strong",
    urgency: "high",
    ...gpaiAddOnRich(gpai),
  };
}

function gpaiAddOnRich(gpai) {
  if (gpai === "customer_chat") return { addOn: TEXT.add_on_chat };
  if (gpai === "synthetic_media") return { addOn: TEXT.add_on_synthetic };
  if (gpai === "train_model") return { addOn: TEXT.add_on_gpai };
  return {};
}

// ---------- Deadline + question helpers ----------

export function daysUntil(date, now = new Date()) {
  if (!date) return null;
  const ms = date.getTime() - now.getTime();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

export function visibleQuestions(answers) {
  return QUESTIONS.filter((q) => !q.showIf || q.showIf(answers));
}

export function isAnswered(question, answers) {
  if (question.optional) return true;
  const v = answers[question.id];
  if (question.type === "multi") return Array.isArray(v) && v.length > 0;
  if (question.type === "text") return true;
  return typeof v === "string" && v.length > 0;
}

// Re-export for convenience.
export { pickLang };
